import { useMemo } from "react";
import { RouteDescription, useGetCheckAccessFunction, useRoutesContext } from "@tecorien/mimic-v2";
import { MenuItemDescription } from "../components/ui";

export type ExtendedMenuItemDescription = Omit<MenuItemDescription, "items"> & {
  id: string,
  items?: ExtendedMenuItemDescription[]
  requiredPermissions?: string | string[]
  requiredOneOfPermissions?: string | string[]
}

export const mapRoutesToMenuItems = (layer: string, routes?: RouteDescription[]): ExtendedMenuItemDescription[] =>
  routes
    ?.filter(x => (x.menuItem?.layer ?? "main") === layer)
    ?.map(x => {
      const items = mapRoutesToMenuItems(layer, x.children);
      const hasPublicChild = items?.some(x =>
        (!x.requiredPermissions || !x.requiredPermissions.length) &&
        (!x.requiredOneOfPermissions || !x.requiredOneOfPermissions.length)
      );
      const childRequiredPermissions = hasPublicChild ? [] : items.flatMap(x => x.requiredPermissions);
      const childRequiredOneOfPermissions = hasPublicChild ? [] : items.flatMap(x => x.requiredOneOfPermissions);

      return ({
        id: x.menuItem ? (x.menuItem?.id ?? x.id ?? x.menuItem?.path ?? x.menuItem?.externalPath ?? x.path) : undefined,
        label: x.menuItem?.label,
        icon: x.menuItem?.icon,
        category: x.menuItem?.category,
        showCategory: (x as any).showCategory ?? !!x.menuItem?.category,
        path: x.menuItem?.path ?? x.path,
        externalPath: x.menuItem?.externalPath,
        items: items,
        requiredPermissions: x.requiredPermissions,
        requiredOneOfPermissions: Array.from(new Set([
          ...(Array.isArray(x.requiredOneOfPermissions) ? x.requiredOneOfPermissions : [ x.requiredOneOfPermissions ]),
          ...(Object.values(x.pageModePermissions || {})
            .flatMap(y => [
              ...(Array.isArray(y.requiredPermissions) ? y.requiredPermissions : [ y.requiredPermissions ]),
              ...(Array.isArray(y.requiredOneOfPermissions) ? y.requiredOneOfPermissions : [ y.requiredOneOfPermissions ]),
            ])
          ),
          ...childRequiredPermissions,
          ...childRequiredOneOfPermissions,
        ].filter(x => x))),
      }) as ExtendedMenuItemDescription;
    })
    .flatMap(x => x.id ? [ x ] : x.items || []) || [];

const filterAvailableMenuItems = (
  menuItems: ExtendedMenuItemDescription[] | undefined,
  checkItemAccess: (requiredPermissions?: (string | string[]), requiredOneOfPermissions?: (string | string[])) => {
    pending: boolean;
    isAccessAllowed: boolean
  },
): ExtendedMenuItemDescription[] =>
  menuItems?.filter(x => checkItemAccess(x.requiredPermissions, x.requiredOneOfPermissions).isAccessAllowed)
    .map(x => ({
      ...x,
      items: filterAvailableMenuItems(x.items, checkItemAccess),
    })) || [];


export function useBuildMenu(layer = "main"): MenuItemDescription[] {
  const routes = useRoutesContext();
  const menu = useMemo(() => mapRoutesToMenuItems(layer, routes), [ layer, routes ]);
  const checkItemAccess = useGetCheckAccessFunction();
  return useMemo(() => filterAvailableMenuItems(menu, checkItemAccess), [ menu, checkItemAccess ]) as MenuItemDescription[];
}